import { ProductModel } from '@mango-engineering/maknet-client-sdk/lib/shop/product/model/product.model';
import { SearchRequest } from '@mango-engineering/maknet-client-sdk/lib/shop/search/types';
import { COUNTRY } from '@mango-engineering/maknet-client-sdk/lib/shop/types';
import { config } from '../../config/env';
import { normalizeLocale, toProductApiLanguage } from '../../types/locale.types';
import { Product, ProductRequest } from '../../types/product.types';
import i18n from '../../utils/i18n';
import { formatProduct } from '../../utils/product.util';
import appSdkClient, { appSdkClientWithAlcoholFlag } from '../../utils/sdk';

export class ProductServices {
  public async getProductById(productRequest: ProductRequest) {
    const { productId, storeCodes, lang, countryCode } = productRequest;
    const locale = normalizeLocale(lang);
    try {
      const productsResponse = await appSdkClient.shop.product.getProductV2(
        productId as string,
        storeCodes,
        toProductApiLanguage(locale),
      );
      return productsResponse ? formatProduct(productsResponse, countryCode, locale) : null;
    } catch {
      return null;
    }
  }

  public async getProductByIds(productRequest: ProductRequest) {
    const {
      productId,
      storeCodes,
      lang,
      isSalesCustomer,
      isEnableRmsStatus,
      is3P,
      membershipId,
      isSameDayAllowedForStore,
      selectedDeliverySlotDate,
      isUnavailableReasonEnabled,
      afcStoreCodes,
      isAfcStoreCodesEnabled,
      countryCode,
      userSegments,
      isHorecaMemberEnabled,
    } = productRequest;
    const locale = normalizeLocale(lang);
    try {
      const productsResponse = await appSdkClient.shop.product.getProductListV2(
        productId as string[], // 1. ids
        storeCodes, // 2. storeCodes
        toProductApiLanguage(locale), // 3. lang
        isSalesCustomer, // 4. isSalesCustomer
        isEnableRmsStatus, // 5. enableRmsStatus
        is3P, // 6. is3P
        membershipId, // 7. membershipId
        true, // 8. shouldFetchDeliveryDescription
        isSameDayAllowedForStore, // 9. isSameDayAllowedForStore
        selectedDeliverySlotDate, // 10. date
        undefined, // 11. isProPointsEnabled
        isUnavailableReasonEnabled, // 12. isUnavailableReasonEnabled
        undefined, // 13. isExpressDeliveryStore
        undefined, // 14. isPromotionPLP
        undefined, // 15. channel
        undefined, // 16. is3PComplexPromotionEnabled
        undefined, // 17. expressStoreCodes
        undefined, // 18. flashSaleStartDate
        undefined, // 19. flashSaleEndDate
        true, // 20. isProductCard
        config.SHOPPING_ENABLE_SECONDARY_PRICING, // 21. isSecondaryPricingEnabled ✅
        true, // 22. isProductOptionsSelectorEnabled
        countryCode, // 23. countryCode ✅
        undefined, // 24. isProductDateFieldEnabled
        true, // 25. loyaltyRm3PromotionFromProductApi
        afcStoreCodes, // 26. afcStoreCodes
        isAfcStoreCodesEnabled, // 27. isAfcStoreCodesEnabled
        undefined, // 28. isOptimizedQueryEnabled
        undefined, // 29. promotionTitleV2Enabled
        config.SHOPPING_ENABLE_PROGRESS_BAR_ON_PRODUCT_CARD, // 30. isShoppingEnabledProgressBarOnTimerProducts
        undefined, // 31. requiredCollectionIds
        userSegments, // 32. userSegments
        isHorecaMemberEnabled, // 33. isHorecaMemberEnabled
      );
      return productsResponse
        ? productsResponse.map((productModel: ProductModel) => formatProduct(productModel, countryCode, locale))
        : [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error in getProductByIds:', error);
      return [];
    }
  }

  public async search(payload: SearchRequest, idToken?: string, allowAlcohol?: boolean) {
    try {
      const response = await appSdkClientWithAlcoholFlag(allowAlcohol).shop.search.searchProducts(payload, idToken);
      return response?.data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Error in search:', error);
      return null;
    }
  }

  public async getProductsByIdsV2(
    productsIds: string[],
    storeCode: string[],
    isExpressDelivery?: boolean,
    isSameDay?: boolean,
    isPromotionPLP?: boolean,
    expressStoreCodes?: string[],
    countryCode?: COUNTRY,
    isPromoTagV2Enabled?: boolean,
    membershipId?: string,
    afcStoreCodes?: string[],
    isAfcStoreCodesEnabled?: boolean,
    userSegments?: string[],
    isHorecaMemberEnabled?: boolean,
  ): Promise<Product[]> {
    const locale = normalizeLocale(i18n.language);
    const productsResponse = await appSdkClient.shop.product.getProductListV2(
      productsIds, // 1. ids
      storeCode, // 2. storeCodes
      toProductApiLanguage(locale), // 3. lang
      undefined, // 4. isSalesCustomer
      undefined, // 5. enableRmsStatus
      undefined, // 6. is3P
      membershipId, // 7. membershipId
      true, // 8. shouldFetchDeliveryDescription
      isSameDay ?? false, // 9. isSameDayAllowedForStore
      undefined, // 10. date
      true, // 11. isProPointsEnabled
      undefined, // 12. isUnavailableReasonEnabled
      isExpressDelivery, // 13. isExpressDeliveryStore
      false, // 14. isPromotionPLP
      undefined, // 15. channel
      true, // 16. is3PComplexPromotionEnabled
      expressStoreCodes, // 17. expressStoreCodes
      undefined, // 18. flashSaleStartDate
      undefined, // 19. flashSaleEndDate
      true, // 20. isProductCard
      config.SHOPPING_ENABLE_SECONDARY_PRICING, // 21. isSecondaryPricingEnabled ✅
      config.SHOPPING_ENABLE_PRODUCT_OPTION_SELECTOR, // 22. isProductOptionsSelectorEnabled
      countryCode, // 23. countryCode
      config.SHOPPING_ENABLE_PRODUCT_NEW_TAG, // 24. isProductDateFieldEnabled
      config.LOYALTY_RM3_PROMOTION_FROM_PRODUCT_API, // 25. loyaltyRm3PromotionFromProductApi
      afcStoreCodes, // 26. afcStoreCodes
      isAfcStoreCodesEnabled, // 27. isAfcStoreCodesEnabled,
      config.ENABLE_PRODUCT_SERVICE_OPTIMIZE_QUERY, // 28. isOptimizedQueryEnabled
      isPromoTagV2Enabled, // 29. isPromoTagV2Enabled
      config.SHOPPING_ENABLE_PROGRESS_BAR_ON_PRODUCT_CARD, // 30. isShoppingEnabledProgressBarOnTimerProducts
      undefined, // 31. requiredCollectionIds
      userSegments, // 32. userSegments
      isHorecaMemberEnabled, // 33. isHorecaMemberEnabled
    );

    return productsResponse.map((productModel) => formatProduct(productModel, countryCode, locale));
  }

  public async getProduct(
    productId: string,
    storeCodes: string[],
    lang: string,
    isSalesCustomer?: boolean,
    is3P?: boolean,
    enableRmsStatus?: boolean,
    membershipId?: string,
    shouldFetchDeliveryDescription?: boolean,
    isSameDayAllowedForStore?: boolean,
    isProPointsEnabled?: boolean,
    selectedDeliverySlotDate?: string,
    isUnavailableReasonEnabled?: boolean,
    isExpress?: boolean,
    is3PComplexPromotionEnabled?: boolean,
    isSecondaryPricingEnabled?: boolean,
    isProductOptionSelectorEnabled?: boolean,
    countryCode?: COUNTRY,
    isProductNewTagEnabled?: boolean,
    shouldFetchPdpVideo?: boolean,
    isLoyaltyRm3PromotionFromProductApiEnabled?: boolean,
    afcStoreCodes?: string[],
    isAfcStoreCodesEnabled?: boolean,
    promotionTitleV2Enabled?: boolean,
    userSegments?: string[],
    isHorecaMemberEnabled?: boolean,
  ) {
    const locale = normalizeLocale(lang);
    // Needs to find actual store after integration
    const product = await appSdkClient.shop.product.getProductV2(
      productId,
      storeCodes,
      toProductApiLanguage(locale),
      isSalesCustomer,
      is3P,
      enableRmsStatus,
      membershipId,
      shouldFetchDeliveryDescription,
      isSameDayAllowedForStore,
      isProPointsEnabled,
      selectedDeliverySlotDate,
      isUnavailableReasonEnabled,
      isExpress,
      undefined,
      is3PComplexPromotionEnabled,
      undefined,
      undefined,
      isSecondaryPricingEnabled,
      isProductOptionSelectorEnabled,
      shouldFetchPdpVideo,
      isProductNewTagEnabled,
      countryCode,
      isLoyaltyRm3PromotionFromProductApiEnabled,
      afcStoreCodes,
      isAfcStoreCodesEnabled,
      undefined, // isOptimizedQueryEnabled
      promotionTitleV2Enabled,
      undefined, // isBiglotEnabled
      userSegments,
      isHorecaMemberEnabled,
    );
    return product ? formatProduct(product, countryCode, locale) : product;
  }
}

export const ProductService = new ProductServices();
export default ProductService;
